import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { useCreateInstitutionRanking } from "../../api/queries/institution-rankings/useCreateInstitutionRanking";
import {
  CreateInstitutionRankingPayload,
  InstitutionRankingValue,
  UpdateInstitutionRankingPayload,
  rankValueOptions,
} from "../../api/types/institution-ranking";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Editor, defaultEditorModules } from "../Editor";
import { useInstitutionRanking } from "../../api/queries/institution-rankings/useInstitutionRanking";
import { useUpdateInstitutionRanking } from "../../api/queries/institution-rankings/useUpdateInstitutionRanking";
import { useDeleteInstitutionRanking } from "../../api/queries/institution-rankings/useDeleteInstitutionRanking";

export default function PlacementEnvironmentInstitutionRankingForm() {
  const { uuid, institutionUuid, rankingUuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createInstituonRanking = useCreateInstitutionRanking();
  const deleteInstituonRanking = useDeleteInstitutionRanking();
  const updateInstituonRanking = useUpdateInstitutionRanking(rankingUuid);
  const { data: rankingDetailResponse } = useInstitutionRanking(rankingUuid);

  const [rankValue, setRankValue] = useState<InstitutionRankingValue>(2);
  const onRankValueChange = (event: DropdownChangeEvent): void =>
    setRankValue(event.value as InstitutionRankingValue);

  const [note, setNote] = useState<string>("");
  const onNoteChange = (value: string): void => setNote(value);

  const onDeleteClick = async () => {
    await deleteInstituonRanking.mutateAsync(rankingUuid);

    queryClient.invalidateQueries(queryKeys.placementEnvironment(uuid), {
      refetchInactive: true,
    });

    navigate(`/journey-trackers/${uuid}`);
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    if (rankingUuid) {
      const payload: UpdateInstitutionRankingPayload = {
        institution: institutionUuid || "",
        rank_value: rankValue,
        note,
      };

      await updateInstituonRanking.mutateAsync(payload);

      queryClient.invalidateQueries(queryKeys.institutionRanking(rankingUuid), {
        refetchInactive: true,
      });
    } else {
      const payload: CreateInstitutionRankingPayload = {
        institution: institutionUuid || "",
        rank_value: rankValue,
        note,
      };

      await createInstituonRanking.mutateAsync(payload);
    }

    queryClient.invalidateQueries(queryKeys.placementEnvironment(uuid), {
      refetchInactive: true,
    });

    navigate(`/journey-trackers/${uuid}`);
  };

  useEffect(() => {
    if (rankingDetailResponse) {
      const { rank_value, note } = rankingDetailResponse.data;
      setRankValue(rank_value);
      setNote(note);
    }
  }, [rankingDetailResponse]);

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <div className="p-fluid">
        <div className="field">
          <label>Rank value:</label>
          <Dropdown
            options={rankValueOptions}
            value={rankValue}
            optionLabel="label"
            optionValue="value"
            onChange={onRankValueChange}
          />
        </div>

        <div className="field">
          <label>Note:</label>
          <Editor
            content={note}
            onChange={onNoteChange}
            modules={defaultEditorModules}
          />
        </div>
      </div>

      <div className="flex gap-1 justify-content-end">
        {rankingUuid && (
          <Button
            type="button"
            label="Delete"
            className="p-button-danger p-button-sm"
            onClick={onDeleteClick}
          />
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
        />
      </div>
    </form>
  );
}
